import React, { useState } from "react";
import LiveScorecards from "./LiveScorecards";
import "./LiveTournamentToolPage.css";
import LiveMovers from "./LiveMovers";
import LiveScorecardFilters from "./LiveScorecardFilters";
import { graphql, useMutation } from "react-relay";
import FlexBreak from "../../../../FlexBreak";
import SelectPGAEvent from "./SelectPGAEvent";
import Tabs from "../../../../Tabs";
import Tab from "../../../../Tabs/Tab";
import WarningBanner from "../../../../WarningBanner";
import { isMobile } from "react-device-detect";
import LivePlayerStats from "./LivePlayerStats";
import { gql, useQuery } from "@apollo/client";
import { Redirect } from "react-router-dom";
import LiveStrokesGainedOverview from "./LiveStrokesGainedOverview";
import WaveSplits from "./WaveSplits";
import HoleScoring from "./HoleScoring";

const UPDATE_MY_PLAYERS = graphql`
  mutation LiveTournamentToolPageUpdateMyPlayersMutation(
    $input: PGAUpdateMyLivePlayersInput!
  ) {
    pgaUpdateMyLivePlayers(input: $input) {
      success
    }
  }
`;

const GET_WHO_AMI = gql`
  query GreenMachinePageWhoAmIQuery {
    whoAmI {
      groups {
        name
      }
      username
    }
  }
`;

export default function LiveTournamentToolPage() {
  const { loading, error, data } = useQuery(GET_WHO_AMI);

  if (!loading) {
    if (!data.whoAmI) {
      return <Redirect to={"/memberships"} />;
    }
  }
  const [myPlayers, setMyPlayers] = useState([]);
  const [updateMyPlayersMutation] = useMutation(UPDATE_MY_PLAYERS);
  const [selectedEventID, setSelectedEventID] = useState(undefined);
  const [filterReloads, setFilterReloads] = useState(0);

  function updateMyPlayersByLineupSetID(lineupSetID) {
    if (!lineupSetID) return;

    updateMyPlayersMutation({
      variables: {
        input: {
          eventId: selectedEventID,
          lineupSetId: lineupSetID,
        },
      },
      onCompleted: () => {
        setFilterReloads(filterReloads + 1);
      },
    });
  }

  function updateMyPlayers(selected, mutate) {
    setMyPlayers([...selected]);

    if (mutate) {
      updateMyPlayersMutation({
        variables: {
          input: {
            eventId: selectedEventID,
            playerIds: selected.map((player) => player.value),
          },
        },
      });
    }
  }

  const strokesGainedTabs = [
    {
      label: "Total",
      backendKey: "total",
    },
    {
      label: "T2G",
      backendKey: "tee_to_green",
    },
    {
      label: "BS",
      backendKey: "ball_striking",
    },
    {
      label: "SG",
      backendKey: "short_game",
    },
    {
      label: "IP",
      backendKey: "iron_play",
    },
    {
      label: "OTT",
      backendKey: "off_the_tee",
    },
    {
      label: "APP",
      backendKey: "approach_the_green",
    },
    {
      label: "ARG",
      backendKey: "around_the_green",
    },
    {
      label: "PUTT",
      backendKey: "putting",
    },
  ];

  return (
    <>
      <div className="pga-live-tournament-tool-page-container">
        <div className="pga-live-tournament-tool-filters">
          <React.Suspense fallback={<p>Loading filters...</p>}>
            <div className="pga-live-tournament-tool-filters-player-search">
              {selectedEventID ? (
                <LiveScorecardFilters
                  myPlayers={myPlayers}
                  updateMyPlayers={updateMyPlayers}
                  updateMyPlayersByLineupSetID={updateMyPlayersByLineupSetID}
                  selectedEventID={selectedEventID}
                  reloads={filterReloads}
                  setReloads={setFilterReloads}
                />
              ) : null}
            </div>
            <div className="pga-live-tournament-tool-filters-event-select">
              <SelectPGAEvent setSelectedEventID={setSelectedEventID} />
            </div>
          </React.Suspense>
        </div>
        <div className="pga-scorecards-and-filters-container">
          {selectedEventID ? (
            <Tabs>
              <Tabs label="Stats">
                <React.Suspense
                  fallback={
                    <div className="pga-scorecards-container">
                      Loading stats...
                    </div>
                  }
                  label="Overview"
                >
                  <LiveStrokesGainedOverview
                    selectedEventID={selectedEventID}
                    myPlayers={myPlayers}
                  />
                </React.Suspense>
                {strokesGainedTabs.map((tab) => {
                  return (
                    <React.Suspense
                      fallback={
                        <div className="pga-scorecards-container">
                          Loading stats...
                        </div>
                      }
                      label={tab.label}
                      key={tab.backendKey}
                    >
                      <LivePlayerStats
                        roundNumber={null}
                        selectedEventID={selectedEventID}
                        strokesGainedType={tab.backendKey}
                        myPlayers={myPlayers}
                      />
                    </React.Suspense>
                  );
                })}
              </Tabs>
              <Tabs label="Scorecards">
                <React.Suspense
                  label={"Event"}
                  fallback={
                    <div className="pga-scorecards-container">
                      Loading scorecards...
                    </div>
                  }
                >
                  <LiveScorecards
                    selectedEventID={selectedEventID}
                    myPlayers={myPlayers}
                    roundNumber={null}
                  />
                </React.Suspense>
                <React.Suspense
                  label={!isMobile ? "Round 1" : "R1"}
                  fallback={
                    <div className="pga-scorecards-container">
                      Loading scorecards...
                    </div>
                  }
                >
                  <LiveScorecards
                    selectedEventID={selectedEventID}
                    myPlayers={myPlayers}
                    roundNumber={1}
                  />
                </React.Suspense>
                <React.Suspense
                  label={!isMobile ? "Round 2" : "R2"}
                  fallback={
                    <div className="pga-scorecards-container">
                      Loading scorecards...
                    </div>
                  }
                >
                  <LiveScorecards
                    selectedEventID={selectedEventID}
                    myPlayers={myPlayers}
                    roundNumber={2}
                  />
                </React.Suspense>
                <React.Suspense
                  label={!isMobile ? "Round 3" : "R3"}
                  fallback={
                    <div className="pga-scorecards-container">
                      Loading scorecards...
                    </div>
                  }
                >
                  <LiveScorecards
                    selectedEventID={selectedEventID}
                    myPlayers={myPlayers}
                    roundNumber={3}
                  />
                </React.Suspense>
                <React.Suspense
                  label={!isMobile ? "Round 4" : "R4"}
                  fallback={
                    <div className="pga-scorecards-container">
                      Loading scorecards...
                    </div>
                  }
                >
                  <LiveScorecards
                    selectedEventID={selectedEventID}
                    myPlayers={myPlayers}
                    roundNumber={4}
                  />
                </React.Suspense>
              </Tabs>
              <React.Suspense
                fallback={
                  <div className="pga-scorecards-container">
                    Loading wave splits...
                  </div>
                }
                label="Wave Splits"
              >
                <WaveSplits eventId={selectedEventID} />
              </React.Suspense>
              <Tabs label="Hole Scoring">
                {[1, 2, 3, 4].map((roundNum) => {
                  return (
                    <React.Suspense
                      key={roundNum}
                      fallback={
                        <div className="pga-scorecards-container">
                          Loading hole scoring...
                        </div>
                      }
                      label={`Round ${roundNum}`}
                    >
                      <HoleScoring
                        eventId={selectedEventID}
                        roundNum={roundNum}
                      />
                    </React.Suspense>
                  );
                })}
              </Tabs>
            </Tabs>
          ) : null}
        </div>

        {/*{selectedEventID ? (*/}
        {/*  <React.Suspense*/}
        {/*    fallback={*/}
        {/*      <div className="pga-live-movers-container">Loading movers...</div>*/}
        {/*    }*/}
        {/*  >*/}
        {/*    <LiveMovers round={2} selectedEventID={selectedEventID} />*/}
        {/*  </React.Suspense>*/}
        {/*) : null}*/}
      </div>
    </>
  );
}
